import { useState } from 'react'
import { Alert } from 'reactstrap'

import { SOURCE, DESTINATION, EDITED, hasTimeCollision } from './incidentMergeFields'
import DatetimeRenderer from '../../Material/DatetimeRenderer'
import './Modals.css'

// Step 1 of the Transfer Incident Data modal: pick which incident's value the surviving
// (destination) incident keeps, one row per column where the two disagree.

const EMPTY = <span className="merge-field-value-empty">— empty</span>

/**
 * A value shown for reference rather than choice. Same shape as a ValueCard so
 * the row lines up with the choices below it, but it is not a button and
 * nothing about it invites a click.
 */
const ReadOnlyCard = ({ children, origin }) => (
    <div className="merge-field-card-wrapper">
        <div className="merge-field-card merge-field-card-readonly" aria-disabled="true">
            <span className="merge-field-value">{children}</span>
            <span className="merge-field-origin">{origin}</span>
        </div>
    </div>
)

// A drag that selects text ends in a click; picking the card then would swap the
// choice (and close an open notes editor) when the user only meant to copy.
const hasTextSelection = () => !!window.getSelection?.()?.toString()

// A div acting as a button rather than a <button>: browsers do not let you select
// text inside a button, and people copy from these cards into the notes editor.
const ValueCard = ({ display, origin, selected, long, expanded, onToggleExpand, onSelect, onEdit }) => (
    <div className="merge-field-card-wrapper">
        <div
            role="button"
            tabIndex={0}
            className={`merge-field-card${selected ? " merge-field-card-selected" : ""}`}
            onClick={() => {
                if (!hasTextSelection()) onSelect()
            }}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    onSelect()
                }
            }}
            aria-pressed={selected}
        >
            <span className={`merge-field-value${long && !expanded ? " merge-field-value-long" : ""}`}>
                {selected && <i className="fa fa-check mr-1" aria-hidden="true" />}
                {display || EMPTY}
            </span>
            <span className="merge-field-origin">{origin}</span>
        </div>
        {((long && display) || onEdit) && (
            <div className="merge-field-card-actions">
                {long && display && (
                    <button type="button" className="merge-field-link-button" onClick={onToggleExpand}>
                        {expanded ? "Show less" : "Show more"}
                    </button>
                )}
                {onEdit && (
                    <button type="button" className="merge-field-link-button" onClick={onEdit}>
                        <i className="fa fa-pencil mr-1" aria-hidden="true" />
                        Edit
                    </button>
                )}
            </div>
        )}
    </div>
)

// Appends a block of text below what is already there, a blank line apart, so pulling
// in the other incident's notes never runs two paragraphs together.
const appendBlock = (current, addition) => {
    if (!addition) return current
    if (!current || !current.trim()) return addition
    return `${current.replace(/\s+$/, "")}\n\n${addition}`
}

/**
 * Free-text editor for an `editable` row. Opened from the Edit link on whichever
 * card was selected, pre-filled with that value; the user can then type, or pull in
 * either incident's value with one click (the usual case: keep both sets of notes).
 */
const FieldEditor = ({ row, value, fromPk, toPk, onChange, onDiscard }) => (
    <div className="merge-field-editor">
        <div className="merge-field-editor-header">
            <span>
                <i className="fa fa-pencil mr-1" aria-hidden="true" />
                Edited {row.label.toLowerCase()}, saved on #{toPk}
            </span>
            <button type="button" className="merge-field-link-button" onClick={onDiscard}>
                Discard edit
            </button>
        </div>
        <textarea
            className="form-control merge-field-editor-textarea"
            aria-label={`Edited ${row.label}`}
            rows={6}
            value={value}
            onChange={(event) => onChange(event.target.value)}
        />
        <div className="merge-field-card-actions">
            {row.fromValue && (
                <button
                    type="button"
                    className="merge-field-link-button"
                    onClick={() => onChange(appendBlock(value, row.fromValue))}
                >
                    <i className="fa fa-plus mr-1" aria-hidden="true" />
                    Add {row.label.toLowerCase()} from #{fromPk}
                </button>
            )}
            {row.toValue && (
                <button
                    type="button"
                    className="merge-field-link-button"
                    onClick={() => onChange(appendBlock(value, row.toValue))}
                >
                    <i className="fa fa-plus mr-1" aria-hidden="true" />
                    Add {row.label.toLowerCase()} from #{toPk}
                </button>
            )}
        </div>
    </div>
)

const IncidentFieldMergeStep = ({
    from, to, differing, identical, resolutions, onResolve, editedValues = {}, onEdit,
}) => {
    const [showIdentical, setShowIdentical] = useState(false)
    const [expandedRows, setExpandedRows] = useState({})
    // The side each open edit started from, so Discard returns to that choice.
    const [editBase, setEditBase] = useState({})

    const fromOrigin = `from #${from?.pk || "--"}`
    const toOrigin = `from #${to?.pk || "--"} (dest)`
    const timeCollision = hasTimeCollision(from, to, resolutions)

    return (
        <>
            <Alert color="info" className="py-2">
                <i className="fa fa-info-circle mr-1" aria-hidden="true" />
                Pick which value <strong>{to?.title || "Untitled incident"} (ID #{to?.pk || "--"})</strong> keeps.
                Anything you leave alone stays as it is on that incident.
            </Alert>

            {/* Which incident is older is what people check before choosing a
                direction, so it leads the step -- read-only, because it is not
                a value the merge can carry across. */}
            <div className="merge-field-row">
                <div className="merge-field-label">Date Added</div>
                <div className="merge-field-options">
                    <ReadOnlyCard origin={fromOrigin}>
                        {from?.dateAdded ? <DatetimeRenderer datetime={from.dateAdded} /> : EMPTY}
                    </ReadOnlyCard>
                    <ReadOnlyCard origin={toOrigin}>
                        {to?.dateAdded ? <DatetimeRenderer datetime={to.dateAdded} /> : EMPTY}
                    </ReadOnlyCard>
                </div>
            </div>

            {differing.length === 0 ? (
                <p className="text-muted small">All comparable fields match on both incidents.</p>
            ) : (
                differing.map((row) => {
                    const selected = resolutions[row.key] || DESTINATION
                    const expanded = !!expandedRows[row.key]
                    const toggleExpand = () =>
                        setExpandedRows((prev) => ({ ...prev, [row.key]: !prev[row.key] }))
                    const editing = selected === EDITED
                    const startEdit = (side) => {
                        if (!row.editable || !onEdit) return undefined
                        return () => {
                            setEditBase((prev) => ({ ...prev, [row.key]: side }))
                            onEdit(row.key, (side === SOURCE ? row.fromValue : row.toValue) || "")
                        }
                    }

                    return (
                        <div className="merge-field-row" key={row.key}>
                            <div className="merge-field-label">{row.label}</div>
                            <div className="merge-field-options">
                                <ValueCard
                                    display={row.fromDisplay}
                                    origin={fromOrigin}
                                    selected={selected === SOURCE}
                                    long={row.long}
                                    expanded={expanded}
                                    onToggleExpand={toggleExpand}
                                    onSelect={() => onResolve(row.key, SOURCE)}
                                    onEdit={selected === SOURCE ? startEdit(SOURCE) : undefined}
                                />
                                <ValueCard
                                    display={row.toDisplay}
                                    origin={toOrigin}
                                    selected={selected === DESTINATION}
                                    long={row.long}
                                    expanded={expanded}
                                    onToggleExpand={toggleExpand}
                                    onSelect={() => onResolve(row.key, DESTINATION)}
                                    onEdit={selected === DESTINATION ? startEdit(DESTINATION) : undefined}
                                />
                            </div>
                            {editing && (
                                <FieldEditor
                                    row={row}
                                    value={editedValues[row.key] ?? ""}
                                    fromPk={from?.pk || "--"}
                                    toPk={to?.pk || "--"}
                                    onChange={(value) => onEdit(row.key, value)}
                                    onDiscard={() => onResolve(row.key, editBase[row.key] || DESTINATION)}
                                />
                            )}
                            {row.key === "incident_time" && timeCollision && (
                                <p className="merge-field-warning">
                                    <i className="fa fa-exclamation-triangle mr-1" aria-hidden="true" />
                                    Both incidents will then share the same car, date, and time. Archive
                                    incident #{from?.pk || "--"} after the transfer.
                                </p>
                            )}
                        </div>
                    )
                })
            )}

            {identical.length > 0 && (
                <div className="mt-3">
                    <button
                        type="button"
                        className="merge-identical-toggle"
                        onClick={() => setShowIdentical((prev) => !prev)}
                    >
                        <i
                            className={`fa fa-chevron-${showIdentical ? "up" : "down"} mr-1`}
                            aria-hidden="true"
                        />
                        {identical.length} identical field{identical.length === 1 ? "" : "s"}
                    </button>
                    {showIdentical && (
                        <ul className="merge-identical-list">
                            {identical.map((row) => (
                                <li key={row.key}>
                                    {row.label}: {row.toDisplay || EMPTY}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </>
    )
}

export default IncidentFieldMergeStep
